
import {
    FetchDocumentOptions,
    IDocument,
    IDocumentCommit,
    IDocumentTransaction,
    MemoDbInterface,
    MemoRemoteInterface
} from '../IMemoRemote';
import {CallableDisposableStack, signal, SignalFunc,
    StackDisposable
} from '../lib/signal';
import {openDb} from '../util/IDBUtils';
import {BlobStore} from "./BlobStore";
import {DocumentStore} from "./DocumentStore";
import {RpcConnFailed} from "../lib/rpc/rpc";
import {PropertyObject} from "../lib";
import {DocumentTransaction, InMemoryDocument, IWritableDocument} from "./WritableDocument";
import {PropertyObjectWithId} from "../lib/memoTypes";
import {IResolver} from "../core/IResolver";

@StackDisposable.Mixin
export class LocalDb implements MemoDbInterface, StackDisposable, IResolver {
    declare [Symbol.dispose]: CallableDisposableStack;
    declare dtor: DisposableStack;

    public readonly remoteItemUpdated = signal<(item: PropertyObject, documentId: string) => void>();
    public readonly errorOccured = signal<(errMes: string) => void>();
    private documentStore: DocumentStore;
    private blobStore: BlobStore;

    public static async open(): Promise<LocalDb> {
        const indexeddb = await openDb("pinboard", 9, function (e) {
            //const loading = EL('div', { idGetOrCreate: 'appLoading' });
            console.info("[DB] Updating DB...");
            if (e.oldVersion < 1) {
                console.info("[DB] Updating DB to v1...");
                this.result.createObjectStore("items", {keyPath: ["document", "id"]});
            }
            if (e.oldVersion < 2) {
                console.info("[DB] Updating DB to v2...");
                this.result.createObjectStore("blobStore", {keyPath: ["id"]});
            }
            if (e.oldVersion < 4) {
                console.info("[DB] Updating DB to v4...");
                const itemStore = this.transaction!!.objectStore("items");
                const request = itemStore.openCursor();
                request.onsuccess = () => {
                    if (request.result) {
                        const v = request.result.value;
                        if (!v.t) {
                            v.t = v.lmt || v.mt;
                            itemStore.put(v);
                        }
                        request.result.continue();
                    }
                };
                itemStore.createIndex("t", "t", {unique: false});
            }
            if (e.oldVersion < 5) {
                console.info("[DB] Updating DB to v5...");
                this.result.createObjectStore("syncConflicts", {keyPath: ["document", "id", "ts"]});
            }
            if (e.oldVersion < 6) {
            }
            if (e.oldVersion < 7) {
                console.info("[DB] Updating DB to v6...");
                if (this.result.objectStoreNames.contains("documents")) this.result.deleteObjectStore("documents");
                if (this.result.objectStoreNames.contains("blobMeta")) this.result.deleteObjectStore("blobMeta");
                this.result.createObjectStore("documents", {keyPath: "document"});
                const blobMeta = this.result.createObjectStore("blobMeta", {keyPath: "id"});
                const itemStore = this.transaction!!.objectStore("items");
                const documentStore = this.transaction!!.objectStore("documents");
                const request = itemStore.openKeyCursor();
                const docIdSet = new Set();
                request.onsuccess = () => {
                    if (request.result) {
                        if (!docIdSet.has(request.result.key[0])) {
                            docIdSet.add(request.result.key[0]);
                            documentStore.put({
                                document: request.result.key[0],
                            })
                        }
                        request.result.continue();
                    }
                };
                const req = this.transaction!!.objectStore("blobStore").openCursor();
                req.onsuccess = () => {
                    if (req.result) {
                        blobMeta.put({
                            id: req.result.key[0],
                            size: req.result.value.blob.length,
                            st: req.result.value.st
                        });
                        req.result.continue();
                    }
                }
            }
            if (e.oldVersion < 9) {
                console.info("[DB] Updating DB to v9...");
                const itemStore = this.transaction!!.objectStore("items");
                itemStore.createIndex("lmt", "lmt", {unique: false});
            }
        });
        return new LocalDb(indexeddb);
    }

    private constructor(public readonly indexedDb: IDBDatabase) {
        indexedDb.onerror = (e) => {
            console.error("db error", e);
            this.errorOccured.emit('Database error: ' + e);
        };
        this.dtor.adopt(this.indexedDb, db => db.close());
        this.blobStore = this.dtor.use(new BlobStore(this.indexedDb));
        this.documentStore = this.dtor.use(new DocumentStore(this.indexedDb));
        this.documentStore.errorOccured.connect(this.errorOccured.emit);
        this.documentStore.remoteItemsUpdated.connect((changes: PropertyObjectWithId[], docId) => {
            if (docId in this.documents) {
                this.documents[docId].then(doc => {
                    if (doc instanceof DbDocument)
                        doc.applyChanges(changes);
                });
            }
        });
        this.documentStore.collectOpenDocuments.connect(() => Object.keys(this.documents));
    }

    private documents: Record<string, Promise<IWritableDocument>> = {};

    getDocument(documentId: string): Promise<IDocument> {
        let  doc = this.documents[documentId];
        if (!doc) {
            doc = this.documents[documentId] = (async () => {
                const {items, lmt, mt} = await this.getItems(documentId);
                const itemHash = Object.fromEntries(items.map(it => [it.id, Object.freeze(it)]));
                const doc = new DbDocument(this.documentStore, documentId, itemHash);
                doc.updated.connect(({ items }) => {
                    for(const item of items)
                        this.remoteItemUpdated.emit(item, documentId);
                });

                const res = this.fetchDocuments({docs: [documentId]}).catch(err => {
                    if (!(err instanceof RpcConnFailed)) throw err;
                });
                if (items.length) {
                    doc.markReady();
                } else {
                    res.then(() => doc.markReady());
                }

                return doc;
            })();
        } else {
            this.fetchDocuments({docs: [documentId]});
        }
        return doc;
    }

    resolve(url: URL, cancel?: AbortSignal): Promise<any> {
        return this.getDocument(url.pathname);
    }

    newSessionDocument(): IDocument {
        const doc = new InMemoryDocument();
        this.documents[doc.documentId] = Promise.resolve(doc);
        return doc;
    }
    newEphemeralDocument(): IDocument {
        return new InMemoryDocument();
    }

    ////////////////////////////////////////////

    public set backingRemote (value: MemoRemoteInterface) {
        this.documentStore.backingRemote = value;
        this.blobStore.backingRemote = value;
    }

    ////////////////////////////////////////////
    // IBlobStore

    getMediaURL(mediaId: string): Promise<string> {
        return this.blobStore.getMediaURL(mediaId);
    }
    retrieveBlob(blobHash: string): Promise<Uint8Array> {
        return this.blobStore.retrieveBlob(blobHash);
    }
    retrieveBlobEx(blobHash: string): Promise<Blob> {
        return this.blobStore.retrieveBlobEx(blobHash);
    }
    storeBlob(blob: Uint8Array): Promise<string> {
        return this.blobStore.storeBlob(blob);
    }

    ////////////////////////////////////////////
    // IDocumentStore

    fetchDocuments(syncOptions: FetchDocumentOptions): Promise<void> {
        return this.documentStore.fetchDocuments(syncOptions);
    }
    get remoteItemsUpdated(): SignalFunc<[PropertyObjectWithId[], string], void> {
        return this.documentStore.remoteItemsUpdated;
    }
    getItems(docId: string): Promise<{ items: any[]; lmt: number; mt: number; }> {
        return this.documentStore.getItems(docId);
    }
    searchItems(filterFunction: (cursor: IDBCursorWithValue) => boolean | object, query?: IDBKeyRange, limit?: number, cancel?: AbortSignal): Promise<any[]> {
        return this.documentStore.searchItems(filterFunction, query, limit, cancel);
    }
    getItem(docId: string, itemId?: string): Promise<any> {
        return this.documentStore.getItem(docId, itemId);
    }
    getDocumentIds(prefix?: string): Promise<string[]> {
        return this.documentStore.getDocumentIds(prefix);
    }
    async onSendObjects(documentId: string, items: { [key: string]: any; }[]): Promise<void> {
        const doc = await this.getDocument(documentId);
        const trans = doc.transact();
        for(const info of items) {
            trans.replace(info);
        }
        await trans.commit();
    }
    ////////////////////////////////////////////
    async clearLocalData(): Promise<undefined> {
        await this.documentStore.clearLocalData();
    }


    //////////////////////////////////////////////
    ///  legacy sync
    getLegacyDocumentSyncManager() {
        return this.documentStore.syncManager;
    }
}

class DbDocument implements IWritableDocument {
    constructor(private readonly store : DocumentStore,
                public readonly documentId: string,
                private readonly _items: Record<string, Readonly<PropertyObject>>) {
        this.ready = new Promise((resolve, reject) => this.markReady = resolve);
    }

    readonly ready : Promise<void>;
    markReady: Function;

    readonly updated = signal<(c: IDocumentCommit) => void>();

    get items(): Readonly<Record<string, Readonly<PropertyObject>>> {
        return this._items;
    }

    get readonly() {
        return false;
    }

    transact(): IDocumentTransaction {
        return new DocumentTransaction(this);
    }

    /**
     * internal: called by DocumentTransaction for changes coming from the frontend
     */
    async performChanges(changes: PropertyObjectWithId[]) {
        for (const newdata of changes) {
            newdata.st = +new Date();
        }
        this.applyChanges(changes);
        await this.store.performChanges(this.documentId, changes);
    }

    /**
     * internal: called by performChanges by changes coming in from the frontend, and from LocalDb for changes coming
     * in from remote
     */
    applyChanges(changes: PropertyObjectWithId[]) {
        for (const newdata of changes) {
            if (newdata.deleted)
                delete this._items[newdata.id];
            else
                this._items[newdata.id] = Object.freeze(newdata);
        }
        this.updated.emit({items: changes});
    }

}